"use strict";

(() => {
  window.zag = window.zag || {};

  class HeroVideoManager {
    constructor(section) {
      this.section = section;
      this.isPlaying = false;
      this.currentVideo = null;

      this.events = new window.zag.managers.EventsManager();
      this.cache = new window.zag.managers.CacheManager();

      this.init();
    }

    init() {
      if (!this.section || !this.videos?.length) return;

      this.setupVideoDetection();
      this.setupPlayPauseControls();
      this.setupVideoEvents();
      this.setupAccessibility();
      this.setupAnalytics();
    }

    /**
     * Returns the videos in the hero section.
     * @returns {NodeList} The video elements.
     */
    get videos() {
      return this.cache.get("dom", "videos", () => {
        return this.section?.querySelectorAll(".media-container video");
      });
    }

    /**
     * Returns the play/pause button.
     * @returns {HTMLElement} The play/pause button element.
     */
    get playPauseButton() {
      return this.cache.get("dom", "playPauseButton", () => {
        return this.section?.querySelector(".play-pause-button__button");
      });
    }

    /**
     * Returns the play icon.
     * @returns {HTMLElement} The play icon element.
     */
    get playIcon() {
      return this.cache.get("dom", "playIcon", () => {
        return this.section?.querySelector(".play__icon");
      });
    }

    /**
     * Returns the pause icon.
     * @returns {HTMLElement} The pause icon element.
     */
    get pauseIcon() {
      return this.cache.get("dom", "pauseIcon", () => {
        return this.section?.querySelector(".pause__icon");
      });
    }

    /**
     * Détection automatique de la vidéo active selon la taille d'écran
     */
    setupVideoDetection() {
      const updateCurrentVideo = () => {
        const isMobile = window.innerWidth < 768;

        this.currentVideo = null;

        this.videos.forEach((video) => {
          const modifiers = new window.zag.managers.ModifiersManager(video);
          const isMobileVideo = modifiers.has("mobile");
          const isDesktopVideo = modifiers.has("desktop");

          if (isMobile && isMobileVideo) {
            this.currentVideo = video;
          } else if (!isMobile && isDesktopVideo) {
            this.currentVideo = video;
          }
        });

        this.updateButtonState();

        // Réinitialiser les événements vidéo pour la nouvelle vidéo
        this.setupVideoEvents();
        this.setupAnalytics();
      };

      // Initialisation
      updateCurrentVideo();

      // Écouter les changements de taille d'écran
      this.events.add(
        window,
        "resize",
        window.zag.utils.debounce(updateCurrentVideo, 250)
      );
    }

    /**
     * Configuration des contrôles play/pause
     */
    setupPlayPauseControls() {
      if (!this.playPauseButton) return;

      this.events.add(this.playPauseButton, "click", (event) => {
        event.preventDefault();
        this.togglePlayPause();
      });

      this.events.add(this.playPauseButton, "keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.togglePlayPause();
        }
      });
    }

    /**
     * Configuration des événements vidéo
     */
    setupVideoEvents() {
      // Supprimer les anciens événements vidéo
      if (this.currentVideo) {
        this.events.remove(this.currentVideo, "play", this.onVideoPlay);
        this.events.remove(this.currentVideo, "pause", this.onVideoPause);
        this.events.remove(this.currentVideo, "ended", this.onVideoEnded);
        this.events.remove(this.currentVideo, "error", this.onVideoError);
      }

      if (!this.currentVideo) return;

      // Ajouter les nouveaux événements
      this.events.add(this.currentVideo, "play", this.onVideoPlay);
      this.events.add(this.currentVideo, "pause", this.onVideoPause);
      this.events.add(this.currentVideo, "ended", this.onVideoEnded);
      this.events.add(this.currentVideo, "error", this.onVideoError);
    }

    // Méthodes pour les événements vidéo
    onVideoPlay = () => {
      this.isPlaying = true;
      this.updateButtonState();
    };

    onVideoPause = () => {
      this.isPlaying = false;
      this.updateButtonState();
    };

    onVideoEnded = () => {
      this.isPlaying = false;
      this.updateButtonState();
    };

    onVideoError = (error) => {
      console.warn("Erreur vidéo hero:", error);
      this.hidePlayPauseButton();
    };

    /**
     * Basculer entre play et pause
     */
    togglePlayPause() {
      if (!this.currentVideo) return;

      try {
        if (this.isPlaying) {
          this.currentVideo.pause();
        } else {
          this.currentVideo.muted = true;
          this.currentVideo.play();
        }

        this.trackVideoAction(this.isPlaying ? "pause" : "play");
      } catch (error) {
        console.warn("Erreur lors du contrôle vidéo:", error);
      }
    }

    /**
     * Mettre à jour l'état visuel du bouton
     */
    updateButtonState() {
      if (!this.playPauseButton || !this.playIcon || !this.pauseIcon) return;

      if (this.isPlaying) {
        this.playPauseButton.classList.add("is-playing");
        this.playIcon.style.display = "none";
        this.pauseIcon.style.display = "block";
      } else {
        this.playPauseButton.classList.remove("is-playing");
        this.playIcon.style.display = "block";
        this.pauseIcon.style.display = "none";
      }
    }

    /**
     * Masquer le bouton play/pause en cas d'erreur
     */
    hidePlayPauseButton() {
      if (this.playPauseButton) {
        this.playPauseButton.style.display = "none";
      }
    }

    /**
     * Configuration de l'accessibilité
     */
    setupAccessibility() {
      if (!this.playPauseButton) return;

      this.events.add(this.playPauseButton, "focus", () => {
        this.playPauseButton.classList.add("is-focused");
      });

      this.events.add(this.playPauseButton, "blur", () => {
        this.playPauseButton.classList.remove("is-focused");
      });

      this.playPauseButton.setAttribute("role", "button");
      this.playPauseButton.setAttribute("tabindex", "0");
      this.updateButtonState();
    }

    /**
     * Analytics pour le tracking des interactions vidéo
     */
    setupAnalytics() {
      // Supprimer les anciens événements analytics
      if (this.currentVideo) {
        this.events.remove(
          this.currentVideo,
          "play",
          this.onVideoPlayAnalytics
        );
        this.events.remove(
          this.currentVideo,
          "pause",
          this.onVideoPauseAnalytics
        );
        this.events.remove(
          this.currentVideo,
          "ended",
          this.onVideoEndedAnalytics
        );
      }

      if (!this.currentVideo) return;

      // Ajouter les nouveaux événements analytics
      this.events.add(this.currentVideo, "play", this.onVideoPlayAnalytics);
      this.events.add(this.currentVideo, "pause", this.onVideoPauseAnalytics);
      this.events.add(this.currentVideo, "ended", this.onVideoEndedAnalytics);
    }

    // Méthodes pour les événements analytics
    onVideoPlayAnalytics = () => {
      this.trackVideoAction("play");
    };

    onVideoPauseAnalytics = () => {
      this.trackVideoAction("pause");
    };

    onVideoEndedAnalytics = () => {
      this.trackVideoAction("ended");
    };

    /**
     * Tracking des actions vidéo
     */
    trackVideoAction(action) {
      const videoSrc = this.currentVideo?.src || "unknown";
      const videoType = this.cache.get("video", "type", () => {
        if (!this.currentVideo) return "unknown";

        const modifiers = new window.zag.managers.ModifiersManager(
          this.currentVideo
        );
        return modifiers.has("mobile") ? "mobile" : "desktop";
      });

      // Tracking Google Analytics
      if (window.gtag) {
        gtag("event", "hero_video_" + action, {
          video_src: videoSrc,
          video_type: videoType,
          section_id: this.section?.id || "unknown",
        });
      }
    }

    /**
     * Méthode pour obtenir les statistiques d'utilisation
     */
    getStats() {
      return {
        totalVideos: this.videos?.length || 0,
        currentVideo: this.currentVideo?.src || null,
        isPlaying: this.isPlaying,
        hasPlayPauseButton: !!this.playPauseButton,
        sectionVisible: this.section?.classList.contains("is-visible") || false,
      };
    }

    /**
     * Méthode pour forcer la lecture
     */
    play() {
      if (this.currentVideo && !this.isPlaying) {
        this.togglePlayPause();
      }
    }

    /**
     * Méthode pour forcer la pause
     */
    pause() {
      if (this.currentVideo && this.isPlaying) {
        this.togglePlayPause();
      }
    }

    destroy() {
      if (this.events) {
        this.events.clear();
      }
    }
  }

  const initHeroVideo = () => {
    const heroSections = document.querySelectorAll(".shopify-section--hero");
    heroSections.forEach((heroSection) => {
      if (heroSection && !heroSection.heroVideoManager) {
        heroSection.heroVideoManager = new HeroVideoManager(heroSection);
      }
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHeroVideo);
  } else {
    initHeroVideo();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.classList.contains("shopify-section--hero")) {
      if (event.target.heroVideoManager) {
        event.target.heroVideoManager.destroy();
      }
      event.target.heroVideoManager = new HeroVideoManager(event.target);
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (event.target.heroVideoManager) {
      event.target.heroVideoManager.destroy();
      delete event.target.heroVideoManager;
    }
  });

  window.HeroVideoManager = HeroVideoManager;
})();
